import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useMobileHeader } from "hocs/MobileHeaderContext";
import usePrefetchRoutes from "hooks/usePrefetchRoutes";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Self-contained search input that keeps its own state in sync with
 * the page via searchRef (avoids re-render loops through context).
 */
const MobileSearchInput = ({ inputRef, searchRef, placeholder, onClose }) => {
  const [value, setValue] = useState(searchRef?.current?.value ?? "");

  const handleChange = (e) => {
    setValue(e.target.value);
    searchRef?.current?.onChange?.(e);
  };

  const handleClear = () => {
    setValue("");
    searchRef?.current?.onChange?.(null);
  };

  return (
    <div className="relative">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-foreground/60 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-background pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-accent transition-colors duration-150"
        >
          <XMarkIcon className="w-4 h-4 text-foreground/60" />
        </button>
      )}
    </div>
  );
};

/**
 * Mobile-only header bar.
 * Desktop navigation is handled by DesktopSidebar — this component
 * is hidden on lg+ via the root `lg:hidden` class.
 */
const Header = () => {
  const { mobileTitle, mobileBack, mobileRedirect, mobileSearch, mobileActions, searchRef } = useMobileHeader();
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const router = useRouter();

  // The back arrow is the most-tapped control on mobile and it navigates with
  // router.push, which never prefetches. Warm its destination as soon as a
  // scene declares one, so going back does not wait on a chunk download.
  usePrefetchRoutes(mobileRedirect ? [mobileRedirect] : [], !!mobileRedirect);

  // Close search bar on route change
  useEffect(() => {
    const handleRouteChange = () => setSearchOpen(false);
    router.events.on("routeChangeStart", handleRouteChange);
    return () => router.events.off("routeChangeStart", handleRouteChange);
  }, [router]);

  const handleBackClick = () => {
    if (mobileRedirect) {
      router.push(mobileRedirect);
    } else {
      router.back();
    }
  };

  // Detail view mode: back arrow + title
  const isDetailView = mobileBack;

  return (
    <div className={`lg:hidden relative z-10 flex-shrink-0 flex flex-col bg-card ${router.pathname === "/search" ? "" : "border-b border-border"}`} style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="px-4 flex items-center h-14 w-full">

        {/* ── Detail view: back arrow + title (+ optional right-side actions) ── */}
        {isDetailView && (
          <>
            <div className="flex items-center min-w-0 flex-1">
              <button
                onClick={handleBackClick}
                aria-label="Back"
                className="mr-2 p-2 -ml-2 rounded-lg hover:bg-muted active:bg-muted-foreground/10 transition-colors duration-150 shrink-0"
              >
                <ArrowLeftIcon className="h-4 w-4 text-foreground" />
              </button>
              {mobileTitle && (
                <h1 className="text-base font-medium text-foreground truncate">
                  {mobileTitle}
                </h1>
              )}
            </div>
            {/* Scene-provided actions (status select, etc.) — pinned right */}
            {mobileActions && (
              <div className="flex items-center gap-2 shrink-0 ml-2">
                {mobileActions}
              </div>
            )}
          </>
        )}

        {/* ── List/default view: logo + centred title ── */}
        {!isDetailView && (
          <>
            <div className="shrink-0">
              <Link href="/valuation/list" legacyBehavior>
                <a>
                  <img
                    src="/image/logo.svg"
                    alt="central valuation"
                    draggable="false"
                    className="h-7 brightness-0 dark:invert"
                  />
                </a>
              </Link>
            </div>
            {mobileTitle && (
              <div className="absolute left-0 right-0 flex justify-center pointer-events-none">
                <h1 className="text-base font-medium text-foreground truncate px-20">
                  {mobileTitle}
                </h1>
              </div>
            )}
          </>
        )}

        {/* ── Search icon — only when page provides search config ── */}
        {!isDetailView && mobileSearch && (
          <button
            onClick={() => {
              if (!searchOpen) {
                setSearchOpen(true);
                requestAnimationFrame(() => searchInputRef.current?.focus());
              } else {
                searchInputRef.current?.blur();
                setSearchOpen(false);
              }
            }}
            className="inline-flex items-center justify-center p-2 ml-auto rounded-lg text-muted-foreground hover:bg-accent active:bg-muted-foreground/10 transition-colors duration-150"
            aria-label={searchOpen ? "Close search" : "Search"}
          >
            {searchOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <MagnifyingGlassIcon className="h-6 w-6" />
            )}
          </button>
        )}
      </div>

      {/* ── Expandable search bar ── */}
      {mobileSearch && (
        <div
          className={`overflow-hidden ${
            searchOpen ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
          }`}
          style={{
            transitionProperty: "max-height, opacity",
            transitionDuration: "var(--cv-transition-slow)",
            transitionTimingFunction: "var(--cv-ease-out)",
          }}
        >
          <div className="px-4 pb-3">
            <MobileSearchInput
              inputRef={searchInputRef}
              searchRef={searchRef}
              placeholder={mobileSearch?.placeholder || "Search..."}
              onClose={() => setSearchOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
